<script setup>
import { ref, watch, nextTick } from 'vue';
import ModalWrapper from './ModalWrapper.vue';
import { rpc } from '../../../composables/useRpc.js';

const props = defineProps({
  show: { type: Boolean, default: false },
  phone: { type: String, default: '' },
  conversationId: { type: String, default: '' },
});

const emit = defineEmits(['close', 'linked']);

const tab = ref('client');
const search = ref('');
const results = ref([]);
const quoteResults = ref([]);
const creating = ref(false);
const loading = ref(false);

// Create form fields
const firstName = ref('');
const lastName = ref('');
const email = ref('');

let debounceTimer = null;

// Reset state when modal opens
watch(() => props.show, (val) => {
  if (val) {
    tab.value = 'client';
    search.value = props.phone || '';
    results.value = [];
    quoteResults.value = [];
    creating.value = false;
    loading.value = false;
    firstName.value = '';
    lastName.value = '';
    email.value = '';
  }
});

// Reset search when switching tabs
watch(tab, (newTab) => {
  search.value = newTab === 'client' ? (props.phone || '') : '';
  results.value = [];
  quoteResults.value = [];
  creating.value = false;
});

// Debounced search
watch(search, (q) => {
  clearTimeout(debounceTimer);
  if (q.length < 2) {
    results.value = [];
    quoteResults.value = [];
    return;
  }
  debounceTimer = setTimeout(() => {
    if (tab.value === 'client') searchClients(q);
    else searchQuotations(q);
  }, 300);
});

async function searchClients(q) {
  try {
    const data = await rpc(`search_clients?q=${encodeURIComponent(q)}`);
    if (Array.isArray(data)) results.value = data;
  } catch {}
}

async function searchQuotations(q) {
  try {
    const data = await rpc(`search-quotations?q=${encodeURIComponent(q)}`);
    if (Array.isArray(data)) quoteResults.value = data;
  } catch {}
}

async function linkClient(clientId) {
  if (loading.value) return;
  loading.value = true;
  try {
    await rpc('link_client', { body: { conversation_id: props.conversationId, client_id: clientId } });
    emit('linked');
    emit('close');
  } catch {} finally {
    loading.value = false;
  }
}

async function linkQuotation(quotationId) {
  if (loading.value) return;
  loading.value = true;
  try {
    await rpc('link-quotation', { body: { conversation_id: props.conversationId, quotation_id: quotationId } });
    emit('linked');
    emit('close');
  } catch {} finally {
    loading.value = false;
  }
}

async function createAndLink() {
  if (!firstName.value.trim() || loading.value) return;
  loading.value = true;
  try {
    await rpc('create_and_link_client', {
      body: {
        conversation_id: props.conversationId,
        first_name: firstName.value.trim(),
        last_name: lastName.value.trim(),
        phone: props.phone,
        email: email.value.trim() || null,
      },
    });
    emit('linked');
    emit('close');
  } catch {} finally {
    loading.value = false;
  }
}

function showCreateForm() {
  creating.value = true;
  nextTick(() => {
    const el = document.querySelector('.aj-link-form input[data-autofocus]');
    if (el) el.focus();
  });
}

function getInitials(client) {
  const f = (client.first_name || '?').charAt(0).toUpperCase();
  const l = (client.last_name || '').charAt(0).toUpperCase();
  return l ? `${f}${l}` : f;
}
</script>

<template>
  <ModalWrapper title="Vincular conversación" :show="show" @close="emit('close')">
    <!-- Tabs -->
    <div class="aj-link-tabs">
      <button
        :class="['aj-link-tab', { active: tab === 'client' }]"
        @click="tab = 'client'"
      >Cliente</button>
      <button
        :class="['aj-link-tab', { active: tab === 'quote' }]"
        @click="tab = 'quote'"
      >Cotización</button>
    </div>

    <!-- Create form -->
    <div v-if="creating" class="aj-link-form">
      <input
        v-model="firstName"
        placeholder="Nombre *"
        data-autofocus
        class="aj-link-input"
      />
      <input
        v-model="lastName"
        placeholder="Apellido"
        class="aj-link-input"
      />
      <input
        :value="phone"
        disabled
        placeholder="Teléfono"
        class="aj-link-input aj-link-input--disabled"
      />
      <input
        v-model="email"
        placeholder="Email"
        type="email"
        class="aj-link-input"
      />
      <div class="aj-link-form-actions">
        <button class="aj-link-btn aj-link-btn--cancel" @click="creating = false">
          Cancelar
        </button>
        <button
          class="aj-link-btn aj-link-btn--primary"
          :disabled="loading || !firstName.trim()"
          @click="createAndLink"
        >
          {{ loading ? '...' : 'Crear y vincular' }}
        </button>
      </div>
    </div>

    <!-- Search + Results -->
    <template v-else>
      <div class="aj-link-search">
        <svg class="aj-link-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
        </svg>
        <input
          v-model="search"
          :placeholder="tab === 'client' ? 'Buscar por nombre o teléfono...' : 'Buscar cotización por nombre o #número...'"
          class="aj-link-search-input"
        />
      </div>

      <div class="aj-link-list">
        <!-- Client results -->
        <template v-if="tab === 'client'">
          <div
            v-for="c in results"
            :key="c.id"
            class="aj-link-item"
            @click="linkClient(c.id)"
          >
            <div class="aj-link-avatar">{{ getInitials(c) }}</div>
            <div class="aj-link-info">
              <span class="aj-link-name">{{ c.first_name }} {{ c.last_name }}</span>
              <span class="aj-link-detail">{{ c.phone }}{{ c.company ? ` — ${c.company}` : '' }}</span>
            </div>
            <svg class="aj-link-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
          </div>
          <div v-if="results.length === 0 && search.length >= 2" class="aj-link-empty">
            Sin resultados
          </div>
        </template>

        <!-- Quotation results -->
        <template v-else>
          <div
            v-for="q in quoteResults"
            :key="q.id"
            class="aj-link-item"
            @click="linkQuotation(q.id)"
          >
            <svg class="aj-link-quote-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            <div class="aj-link-info">
              <span class="aj-link-name">#{{ q.number }} {{ q.title }}</span>
              <span v-if="q.client_name" class="aj-link-detail">{{ q.client_name }}</span>
            </div>
            <span :class="['aj-link-status', `aj-link-status--${q.status}`]">{{ q.status }}</span>
          </div>
          <div v-if="quoteResults.length === 0 && search.length >= 2" class="aj-link-empty">
            Sin resultados
          </div>
        </template>
      </div>

      <!-- Create new button (client tab only) -->
      <div v-if="tab === 'client'" class="aj-link-footer">
        <button class="aj-link-btn aj-link-btn--create" @click="showCreateForm">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="8.5" cy="7" r="4"/>
            <line x1="20" y1="8" x2="20" y2="14"/>
            <line x1="23" y1="11" x2="17" y2="11"/>
          </svg>
          Crear nuevo contacto
        </button>
      </div>
    </template>
  </ModalWrapper>
</template>

<style scoped>
.aj-link-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
}
.aj-link-tab {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  background: rgb(var(--slate-3, 240 240 243));
  color: rgb(var(--slate-11, 96 100 108));
  transition: all 0.15s ease;
}
.aj-link-tab.active {
  background: rgb(var(--blue-9, 0 100 230));
  color: #fff;
}
.aj-link-tab:hover:not(.active) {
  background: rgb(var(--slate-4, 232 232 236));
}

.aj-link-search {
  position: relative;
  margin-bottom: 12px;
}
.aj-link-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: rgb(var(--slate-9, 142 142 147));
  pointer-events: none;
}
.aj-link-search-input {
  width: 100%;
  padding: 10px 12px 10px 36px;
  border-radius: 10px;
  border: 1px solid rgb(var(--slate-4, 232 232 236));
  background: rgb(var(--slate-2, 248 248 249));
  font-size: 13px;
  color: rgb(var(--slate-12, 28 32 36));
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.aj-link-search-input:focus {
  border-color: rgb(var(--blue-8, 0 100 200));
}
.aj-link-search-input::placeholder {
  color: rgb(var(--slate-9, 142 142 147));
}

.aj-link-list {
  max-height: 36vh;
  overflow-y: auto;
  margin: 0 -20px;
  padding: 0 20px;
}

.aj-link-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.12s;
}
.aj-link-item:hover {
  background: rgb(var(--slate-3, 240 240 243));
}
.aj-link-item:active {
  background: rgb(var(--slate-4, 232 232 236));
}

.aj-link-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgb(var(--blue-3, 214 230 255));
  color: rgb(var(--blue-11, 0 62 163));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.aj-link-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.aj-link-name {
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--slate-12, 28 32 36));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.aj-link-detail {
  font-size: 12px;
  color: rgb(var(--slate-9, 142 142 147));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.aj-link-action-icon {
  width: 16px;
  height: 16px;
  color: rgb(var(--slate-8, 160 162 168));
  flex-shrink: 0;
}

.aj-link-quote-icon {
  width: 18px;
  height: 18px;
  color: rgb(var(--blue-9, 0 100 230));
  flex-shrink: 0;
}

.aj-link-status {
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
  flex-shrink: 0;
  text-transform: capitalize;
}
.aj-link-status--borrador {
  background: rgb(var(--slate-3, 240 240 243));
  color: rgb(var(--slate-11, 96 100 108));
}
.aj-link-status--enviada {
  background: rgb(var(--blue-3, 214 230 255));
  color: rgb(var(--blue-11, 0 62 163));
}
.aj-link-status--aprobada {
  background: rgba(34, 197, 94, 0.12);
  color: #16a34a;
}
.aj-link-status--rechazada {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}
.aj-link-status--cotizado {
  background: rgba(234, 179, 8, 0.12);
  color: #a16207;
}

.aj-link-empty {
  text-align: center;
  padding: 24px 12px;
  font-size: 13px;
  color: rgb(var(--slate-9, 142 142 147));
}

.aj-link-footer {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgb(var(--slate-4, 232 232 236));
}

.aj-link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
}
.aj-link-btn--create {
  width: 100%;
  background: rgb(var(--slate-3, 240 240 243));
  color: rgb(var(--slate-12, 28 32 36));
}
.aj-link-btn--create:hover {
  background: rgb(var(--slate-4, 232 232 236));
}
.aj-link-btn--primary {
  background: rgb(var(--blue-9, 0 100 230));
  color: #fff;
}
.aj-link-btn--primary:hover:not(:disabled) {
  background: rgb(var(--blue-10, 0 80 200));
}
.aj-link-btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.aj-link-btn--cancel {
  background: rgb(var(--slate-3, 240 240 243));
  color: rgb(var(--slate-11, 96 100 108));
}
.aj-link-btn--cancel:hover {
  background: rgb(var(--slate-4, 232 232 236));
}

.aj-link-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.aj-link-input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgb(var(--slate-4, 232 232 236));
  background: rgb(var(--slate-2, 248 248 249));
  font-size: 13px;
  color: rgb(var(--slate-12, 28 32 36));
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.aj-link-input:focus {
  border-color: rgb(var(--blue-8, 0 100 200));
}
.aj-link-input::placeholder {
  color: rgb(var(--slate-9, 142 142 147));
}
.aj-link-input--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.aj-link-form-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}
.aj-link-form-actions .aj-link-btn {
  flex: 1;
}
</style>
